import { Button } from "@istari-frontend/ui";
import { AlertTriangle } from "lucide-react";
import React from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

interface ContentErrorBoundaryProps {
  children: React.ReactNode;
  resetKeys?: unknown[];
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  onReset?: () => void;
}

export function ContentErrorBoundary({
  children,
  resetKeys,
  title,
  description,
  icon,
  onReset,
}: ContentErrorBoundaryProps) {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ContentErrorFallback {...props} title={title} description={description} icon={icon} />
      )}
      resetKeys={resetKeys}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}

interface StatusCardProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}

const StatusCard: React.FC<StatusCardProps> = ({ icon, title, description, action }) => (
  <div className="flex h-full min-w-0 flex-1 flex-col items-center px-6 pt-[20vh]">
    <div className="flex min-w-0 flex-col items-center justify-center gap-6 rounded-lg p-6 text-center text-balance md:p-12">
      <div className="flex max-w-sm flex-col items-center gap-2 text-center">
        <div className="bg-muted text-foreground mb-2 flex size-10 shrink-0 items-center justify-center rounded-lg [&_svg]:size-6">
          {icon}
        </div>
        <div className="text-lg font-medium tracking-tight">{title}</div>
        <div className="text-muted-foreground text-sm/relaxed">{description}</div>
      </div>
      {action && <div className="flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm">{action}</div>}
    </div>
  </div>
);

interface ContentErrorFallbackProps extends FallbackProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}

function ContentErrorFallback({
  error,
  resetErrorBoundary,
  title = "Something went wrong",
  description = "An error occurred while rendering this content.",
  icon = <AlertTriangle />,
}: ContentErrorFallbackProps) {
  return (
    <StatusCard
      icon={icon}
      title={title}
      description={
        <div className="flex flex-col items-center gap-3">
          <span>{description}</span>
          <details className="text-muted-foreground w-full max-w-xs text-left text-xs">
            <summary className="hover:text-foreground">Reveal error</summary>
            <pre className="bg-muted mt-2 overflow-auto rounded p-2 whitespace-pre-wrap">{error.message}</pre>
          </details>
        </div>
      }
      action={
        <Button variant="outline" size="sm" onClick={resetErrorBoundary}>
          Retry
        </Button>
      }
    />
  );
}
